import React, { useEffect, useState } from 'react';
import { Typography, Spin, Image } from 'antd';
import { LoadingOutlined, PictureOutlined, ExpandAltOutlined } from '@ant-design/icons';
import api from '../../../api/client';

const { Text } = Typography;

const MediaGallery = () => {
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMedia = async () => {
            try {
                const response = await api.get('/storage/files/media-generations');
                // Filter for images only for now, or handle videos if needed.
                // Based on Editor.js, it seems to return a mix.
                const items = response.data.map(file => ({
                    id: file.path,
                    src: file.url,
                    name: file.name,
                    type: file.type || 'image' // Assuming type might be available or we treat all as displayable
                }));
                // Duplicate the list to ensure seamless scrolling if we have enough items
                // If we have very few items, we might need to duplicate more times.
                setMedia(items);
            } catch (error) {
                console.error('Failed to fetch gallery media:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMedia();
    }, []);

    const [opacity, setOpacity] = useState(0);

    useEffect(() => {
        if (!loading && media.length > 0) {
            // Small timeout to ensure DOM is ready and transition triggers
            const timer = setTimeout(() => setOpacity(1), 50);
            return () => clearTimeout(timer);
        }
    }, [loading, media]);

    if (loading) return null;
    if (media.length === 0) return null;

    // We need enough items to scroll smoothly. If less than 10, repeat them a few times.
    const displayMedia = media.length < 10 ? [...media, ...media, ...media, ...media] : [...media, ...media];

    return (
        <div style={{
            marginBottom: 40,
            marginTop: 10,
            opacity: opacity,
            transition: 'opacity 0.8s ease-in-out'
        }}>


            <div className="media-gallery-container" style={{
                position: 'relative',
                overflow: 'hidden',
                maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
                WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
            }}>
                <div className="media-gallery-track" style={{
                    display: 'flex',
                    gap: 16,
                    padding: '0',
                    width: 'max-content',
                    animation: 'scroll 60s linear infinite',
                }}>
                    {displayMedia.map((item, index) => (
                        <div key={`${item.id}-${index}`} style={{
                            width: 200,
                            height: 200, // Square aspect ratio looks modern
                            flexShrink: 0,
                            borderRadius: 12,
                            overflow: 'hidden',
                            border: '1px solid #30363d',
                            background: '#161b22',
                            position: 'relative',
                            transition: 'transform 0.2s',
                        }}>
                            {item.type === 'video' ? (
                                <video
                                    src={item.src}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    muted
                                    loop
                                    onMouseOver={e => e.target.play()}
                                    onMouseOut={e => e.target.pause()}
                                />
                            ) : (
                                <div
                                    style={{ position: 'relative', width: '100%', height: '100%' }}
                                    className="gallery-item-wrapper"
                                >
                                    <Image
                                        src={item.src}
                                        alt={item.name}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        height={'100%'}
                                        width={'100%'}
                                        preview={{
                                            mask: (
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 8
                                                }}>
                                                    <ExpandAltOutlined style={{ fontSize: 24 }} />
                                                    <span style={{ fontSize: 12 }}>View</span>
                                                </div>
                                            )
                                        }}
                                    />
                                </div>
                            )}
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                padding: 12,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                pointerEvents: 'none' // Let clicks pass through to the image
                            }} className="media-info">
                                <Text style={{ color: '#fff', fontSize: 12 }} ellipsis>{item.name}</Text>
                            </div>
                        </div>
                    ))}
                </div>

                <style>{`
                    @keyframes scroll {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    .media-gallery-container:hover .media-gallery-track {
                        animation-play-state: paused;
                    }
                    .media-gallery-track > div:hover {
                        transform: scale(1.02);
                        z-index: 10;
                        border-color: #1890ff;
                    }
                    .media-gallery-track > div:hover .media-info {
                        opacity: 1;
                    }
                    /* Ensure antd image fits the container */
                    .ant-image {
                        width: 100%;
                        height: 100%;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default MediaGallery;
